import type { FC } from '../../lib/teact/teact';
import React, {
  useCallback, useEffect, useRef, useState,
} from '../../lib/teact/teact';

import type { ThemeKey } from '../../types';

import { TURNSTILE_SITE_KEY } from '../../config';
import { getAuthCopy } from '../../util/authI18n';

import styles from './TurnstileGate.module.scss';

type OwnProps = {
  theme: ThemeKey;
  onPass: NoneToVoidFunction;
};

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-api';
const TURNSTILE_SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

export function hasPassedTurnstileGate() {
  return false;
}

const TurnstileGate: FC<OwnProps> = ({
  theme,
  onPass,
}) => {
  const authCopy = getAuthCopy();
  const widgetRef = useRef<string>();
  const containerRef = useRef<HTMLDivElement>(null);

  const [isApiReady, setIsApiReady] = useState(Boolean(window.turnstile));
  const [retryKey, setRetryKey] = useState(0);
  const [statusText, setStatusText] = useState(authCopy.gateLoading);
  const [errorText, setErrorText] = useState<string>();

  useEffect(() => {
    document.title = authCopy.accountSecurityTitle;
  }, [authCopy.accountSecurityTitle]);

  useEffect(() => {
    if (window.turnstile) {
      // #region debug-point C:turnstile-api-already-ready
      fetch('http://127.0.0.1:7777/event', {
        method: 'POST',
        body: JSON.stringify({
          sessionId: 'country-code-network',
          runId: 'pre-fix',
          hypothesisId: 'C',
          location: 'TurnstileGate.tsx:40',
          msg: '[DEBUG] turnstile api already present',
          data: {},
          ts: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      setIsApiReady(true);
      return undefined;
    }

    let script = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;

    const handleLoad = () => {
      // #region debug-point C:turnstile-script-loaded
      fetch('http://127.0.0.1:7777/event', {
        method: 'POST',
        body: JSON.stringify({
          sessionId: 'country-code-network',
          runId: 'pre-fix',
          hypothesisId: 'C',
          location: 'TurnstileGate.tsx:61',
          msg: '[DEBUG] turnstile script loaded',
          data: {},
          ts: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      setIsApiReady(true);
      setStatusText(authCopy.gatePrompt);
    };

    const handleError = () => {
      // #region debug-point C:turnstile-script-error
      fetch('http://127.0.0.1:7777/event', {
        method: 'POST',
        body: JSON.stringify({
          sessionId: 'country-code-network',
          runId: 'pre-fix',
          hypothesisId: 'C',
          location: 'TurnstileGate.tsx:76',
          msg: '[DEBUG] turnstile script failed to load',
          data: {},
          ts: Date.now(),
        }),
      }).catch(() => {});
      // #endregion
      setErrorText(authCopy.gateLoadError);
      setStatusText(authCopy.gateLoadFailed);
    };

    if (!script) {
      script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);
      document.head.appendChild(script);
    } else {
      script.addEventListener('load', handleLoad);
      script.addEventListener('error', handleError);
    }

    return () => {
      script?.removeEventListener('load', handleLoad);
      script?.removeEventListener('error', handleError);
    };
  }, [authCopy.gateLoadError, authCopy.gateLoadFailed, authCopy.gatePrompt]);

  useEffect(() => {
    const handleContextMenu = (event: MouseEvent) => {
      event.preventDefault();
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toUpperCase();
      const shouldBlock = key === 'F12'
        || (event.ctrlKey && key === 'U')
        || (event.ctrlKey && event.shiftKey && ['I', 'J', 'C'].includes(key));

      if (shouldBlock) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    document.addEventListener('contextmenu', handleContextMenu, true);
    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu, true);
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, []);

  useEffect(() => {
    if (!isApiReady || !containerRef.current || !window.turnstile) {
      return undefined;
    }
    // #region debug-point C:turnstile-render-start
    fetch('http://127.0.0.1:7777/event', {
      method: 'POST',
      body: JSON.stringify({
        sessionId: 'country-code-network',
        runId: 'pre-fix',
        hypothesisId: 'C',
        location: 'TurnstileGate.tsx:143',
        msg: '[DEBUG] turnstile render start',
        data: {
          retryKey,
          hasExistingWidget: Boolean(widgetRef.current),
        },
        ts: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    if (widgetRef.current) {
      window.turnstile.remove(widgetRef.current);
      widgetRef.current = undefined;
    }

    containerRef.current.innerHTML = '';
    setErrorText(undefined);
    setStatusText(authCopy.gatePrompt);

    widgetRef.current = window.turnstile.render(containerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      theme: theme === 'dark' ? 'dark' : 'light',
      appearance: 'always',
      callback: () => {
        // #region debug-point C:turnstile-callback-success
        fetch('http://127.0.0.1:7777/event', {
          method: 'POST',
          body: JSON.stringify({
            sessionId: 'country-code-network',
            runId: 'pre-fix',
            hypothesisId: 'C',
            location: 'TurnstileGate.tsx:174',
            msg: '[DEBUG] turnstile callback success',
            data: {},
            ts: Date.now(),
          }),
        }).catch(() => {});
        // #endregion
        setStatusText(authCopy.gatePassed);
        onPass();
      },
      'error-callback': () => {
        // #region debug-point C:turnstile-callback-error
        fetch('http://127.0.0.1:7777/event', {
          method: 'POST',
          body: JSON.stringify({
            sessionId: 'country-code-network',
            runId: 'pre-fix',
            hypothesisId: 'C',
            location: 'TurnstileGate.tsx:187',
            msg: '[DEBUG] turnstile callback error',
            data: {},
            ts: Date.now(),
          }),
        }).catch(() => {});
        // #endregion
        setErrorText(authCopy.gateVerifyFailed);
        setStatusText(authCopy.gateVerifyFailedStatus);
      },
      'expired-callback': () => {
        setErrorText(authCopy.gateExpired);
        setStatusText(authCopy.gateExpiredStatus);
      },
      'timeout-callback': () => {
        setErrorText(authCopy.gateTimeout);
        setStatusText(authCopy.gateTimeoutStatus);
      },
    });

    return () => {
      if (widgetRef.current && window.turnstile) {
        // #region debug-point C:turnstile-cleanup-remove
        fetch('http://127.0.0.1:7777/event', {
          method: 'POST',
          body: JSON.stringify({
            sessionId: 'country-code-network',
            runId: 'pre-fix',
            hypothesisId: 'C',
            location: 'TurnstileGate.tsx:220',
            msg: '[DEBUG] turnstile cleanup remove widget',
            data: {
              widgetId: widgetRef.current,
            },
            ts: Date.now(),
          }),
        }).catch(() => {});
        // #endregion
        window.turnstile.remove(widgetRef.current);
        widgetRef.current = undefined;
      }
    };
  }, [
    authCopy.gateExpired,
    authCopy.gateExpiredStatus,
    authCopy.gatePassed,
    authCopy.gatePrompt,
    authCopy.gateTimeout,
    authCopy.gateTimeoutStatus,
    authCopy.gateVerifyFailed,
    authCopy.gateVerifyFailedStatus,
    isApiReady,
    onPass,
    retryKey,
    theme,
  ]);

  const handleRetry = useCallback(() => {
    setErrorText(undefined);
    setRetryKey((current) => current + 1);
  }, []);

  const cardClassName = theme === 'dark'
    ? `${styles.card} ${styles.dark}`
    : styles.card;
  const statusClassName = errorText
    ? `${styles.status} ${styles.error}`
    : styles.status;

  return (
    <div className={styles.gate}>
      <div className={cardClassName}>
        <p className={styles.desc}>
          {authCopy.gateDescription}
        </p>
        <div ref={containerRef} className={styles.widgetWrap} />
        <div className={statusClassName}>{errorText || statusText}</div>
        {errorText && (
          <button type="button" className={styles.retry} onClick={handleRetry}>
            {authCopy.gateRetry}
          </button>
        )}
      </div>
    </div>
  );
};

export default TurnstileGate;
